import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { LockIcon } from './contacts/LockIcon'

export type TabType = {
  name: string
  totalItems?: string
  tab: string
  isLocked?: boolean
  hasNotification?: boolean
}

type Args = {
  tabs: TabType[]
  className?: string
  onTabChange: (tab: string) => void
  /**
   * Seeds the selection once (the seeded ref guards against re-runs from an
   * unstable `onTabChange`); after that the strip is uncontrolled.
   */
  initialTab?: string
  /**
   * Enable a horizontally-scrollable strip with chevron controls that appear
   * only when there is more to reveal. Used on mobile so a long tab list stays
   * one clean row instead of wrapping or showing a raw scrollbar. Omit for the
   * plain (always-fits) desktop row.
   */
  scrollable?: boolean
}

function TabBadge({ tab, isSelected }: { tab: TabType; isSelected: boolean }) {
  if (tab.isLocked) return <LockIcon />
  if (!tab.totalItems) return null
  return (
    <span
      className={classNames(
        `flex h-5 items-center justify-center rounded border-[0.5px] px-1.5 pt-px text-label-xs leading-none`,
        tab.hasNotification
          ? `highlight-pulse-wrapper border-primary-200 bg-primary-100 text-primary-700`
          : `border-default ${isSelected ? `text-on-color bg-primary-600` : `bg-gray-minimal text-gray-strong`}`,
      )}
    >
      {tab.totalItems}
    </span>
  )
}

export default function InlineTabs({ tabs, className, onTabChange, scrollable = false, initialTab }: Args) {
  const [selectedTab, setSelectedTab] = useState<string>(tabs[0].tab || ``)
  const seeded = useRef(false)

  useEffect(() => {
    if (seeded.current || !initialTab) return
    if (!tabs.some((candidate) => candidate.tab === initialTab)) return
    seeded.current = true
    setSelectedTab(initialTab)
    // Tell the parent too, so its content and this strip cannot disagree.
    onTabChange(initialTab)
  }, [initialTab, tabs, onTabChange])
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const selectTab = (tab: string) => {
    setSelectedTab(tab)
    onTabChange(tab)
  }

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    if (!scrollable) return
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener(`scroll`, updateScrollState, { passive: true })
    // Covers a pane resize and a rotation too: both change this element's own
    // box, which is what the observer watches. A window listener beside it was
    // redundant.
    const observer = new ResizeObserver(updateScrollState)
    observer.observe(el)
    return () => {
      el.removeEventListener(`scroll`, updateScrollState)
      observer.disconnect()
    }
  }, [scrollable, updateScrollState, tabs.length])

  // Bring the selected tab into the strip's view whenever it changes, including when it is
  // seeded from `initialTab`: a drawer opened on its last tab otherwise showed that tab's
  // content with the tab itself scrolled off the edge. Horizontal only, and only as far as
  // needed to clear the chevrons, so the page itself never jumps.
  useEffect(() => {
    if (!scrollable) return
    const el = scrollRef.current
    const button = [...(el?.children ?? [])].find((child) => (child as HTMLElement).dataset.tab === selectedTab) as
      | HTMLElement
      | undefined
    if (!el || !button) return
    const CHEVRON_ROOM = 36
    const strip = el.getBoundingClientRect()
    const tab = button.getBoundingClientRect()
    if (tab.right > strip.right - CHEVRON_ROOM) el.scrollLeft += tab.right - strip.right + CHEVRON_ROOM
    // Never below zero: at the start there is no left chevron to clear.
    else if (tab.left < strip.left + CHEVRON_ROOM)
      el.scrollLeft = Math.max(0, el.scrollLeft - (strip.left + CHEVRON_ROOM - tab.left))
  }, [selectedTab, scrollable])

  const scrollBy = (direction: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: direction * 140, behavior: `smooth` })
  }

  const tabButtonClass = (isSelected: boolean) =>
    classNames(
      className,
      /*
       * No underline. The strip's padding is set by the caller and the header's
       * divider moves with it, so a bottom border meant two different things: on
       * a tab with filter chips it was stranded mid-header under a single word,
       * and on a tab without them it sat 1px above the divider as a double line.
       *
       * The badge already carries the state, going from grey to a filled blue,
       * and the label goes from muted to full contrast. Two signals on the tab
       * itself, neither of which depends on where the divider happens to be.
       *
       * Colour rather than weight: bolding the selected label changes its width
       * and shuffles every tab to its right on each switch.
       */
      isSelected ? `text-strong` : `text-subtle hover:text-default`,
      `shrink-0 whitespace-nowrap bg-transparent px-1 pb-3 pt-0.5 text-ui-sm`,
    )

  const tabButtons = tabs?.map((tab) => (
    <button
      key={tab.name}
      data-tab={tab.tab}
      className={tabButtonClass(tab.tab === selectedTab)}
      onClick={() => selectTab(tab.tab)}
      disabled={tab.isLocked}
    >
      <div className="flex items-center gap-x-2 align-middle">
        <span>{tab.name}</span>
        <TabBadge tab={tab} isSelected={tab.tab === selectedTab} />
      </div>
    </button>
  ))

  if (!scrollable) {
    return (
      <div className="block">
        <div className="flex space-x-4" aria-label="Tabs">
          {tabButtons}
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      {/*
        The chevrons sit on top of the strip, so without this the tab underneath
        renders half-hidden behind the gradient and reads as broken text
        ("Inbox" showing as "ox"). Reserve the space only while a chevron is
        actually up, so a strip that fits keeps its full width.
      */}
      <div
        ref={scrollRef}
        className={classNames(
          `flex space-x-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`,
          canScrollLeft && `pl-9`,
          canScrollRight && `pr-9`,
        )}
        aria-label="Tabs"
      >
        {tabButtons}
      </div>

      {/*
        items-start, not items-center: the tabs carry their spare height as
        bottom padding, so centering in the strip drops the chevron below the
        labels. The full-height gradient still masks a tab scrolling under it.
      */}
      {canScrollLeft && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          onClick={() => scrollBy(-1)}
          className="absolute left-0 top-0 flex h-full items-start bg-gradient-to-r from-(--surface) via-(--surface) to-transparent pr-4 pt-0.5 text-subtle hover:text-strong"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          onClick={() => scrollBy(1)}
          className="absolute right-0 top-0 flex h-full items-start bg-gradient-to-l from-(--surface) via-(--surface) to-transparent pl-4 pt-0.5 text-subtle hover:text-strong"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  )
}
